import { WebRTCStats } from '@peermetrics/webrtc-stats';

import {
  calculateCallScore,
  convertStatsObjectToCallStats,
  getLatestConnectionMetrics,
  sendWebRTCCallScore,
} from 'features/analytics/helpers/';
import type {
  WebRTCStatsData,
  ConnectionInfo,
  ConnectionMetrics,
} from 'features/analytics/interfaces/';

const DISCONNECTED_STATES = ['closed', 'failed', 'disconnected'] as const;
type DisconnectedState = (typeof DISCONNECTED_STATES)[number];

export class WebRTCCallQualityAnalytics {
  private static instance: WebRTCCallQualityAnalytics | null = null;

  private readonly connections = new Map<string, ConnectionInfo>();
  private readonly dispatch: ((action: any) => void) | null;
  private readonly webrtcStats: WebRTCStats;

  private constructor(dispatch: ((action: any) => void) | null) {
    this.dispatch = dispatch;
    this.webrtcStats = new WebRTCStats({
      getStatsInterval: 5000,
      rawStats: false,
      statsObject: true,
      filteredStats: false,
      remote: true, // Include remote stats for jitter and RTT
      wrapGetUserMedia: false,
    });
  }

  public static getInstance(
    dispatch?: ((action: any) => void) | null
  ): WebRTCCallQualityAnalytics {
    if (!WebRTCCallQualityAnalytics.instance) {
      WebRTCCallQualityAnalytics.instance = new WebRTCCallQualityAnalytics(
        dispatch || null
      );
    }

    return WebRTCCallQualityAnalytics.instance;
  }

  public static destroyInstance(): void {
    if (WebRTCCallQualityAnalytics.instance) {
      WebRTCCallQualityAnalytics.instance.cleanup();
      WebRTCCallQualityAnalytics.instance = null;
    }
  }

  // Connection Management
  public addConnection(
    pc: RTCPeerConnection,
    peerId: string,
    callId?: number
  ): void {
    if (this.connections.has(peerId)) {
      console.warn(`Connection for peer ${peerId} already exists`);
      return;
    }

    const connectionInfo: ConnectionInfo = {
      peerId,
      pc,
      webrtcStats: this.webrtcStats,
      callId,
    };

    this.webrtcStats.addConnection({ pc, peerId });
    this.setupEventListeners(peerId);
    this.connections.set(peerId, connectionInfo);
  }

  public removeConnection(peerId: string): void {
    const connection = this.connections.get(peerId);
    if (!connection) {
      return;
    }

    try {
      this.webrtcStats.removeConnection({ peerId, pc: connection.pc });
    } catch (error) {
      console.error(`Error removing connection for peer ${peerId}:`, error);
    }

    this.connections.delete(peerId);
  }

  public cleanup(): void {
    // Clean up all connections first
    for (const [peerId, connection] of this.connections.entries()) {
      try {
        this.webrtcStats.removeConnection({ peerId, pc: connection.pc });
      } catch (error) {
        console.error(`Error removing connection for peer ${peerId}:`, error);
      }
    }

    this.connections.clear();

    // Destroy the WebRTCStats instance
    try {
      this.webrtcStats.destroy();
    } catch (error) {
      console.error('Error destroying WebRTCStats instance:', error);
    }
  }

  // Event Handlers
  private setupEventListeners(peerId: string): void {
    this.webrtcStats.on('stats', (event) =>
      this.handleStatsEvent(event, peerId)
    );
    this.webrtcStats.on('connection', (event) =>
      this.handleConnectionEvent(event, peerId)
    );
  }

  private handleStatsEvent(
    event: { peerId: string; data: WebRTCStatsData },
    targetPeerId: string
  ): void {
    if (event.peerId === targetPeerId) {
      this.updateLatestStats(targetPeerId, event.data);
    }
  }

  private handleConnectionEvent(
    event: { peerId: string; event: string; data: string },
    targetPeerId: string
  ): void {
    if (
      event.peerId === targetPeerId &&
      event.event === 'onconnectionstatechange' &&
      this.isDisconnectedState(event.data)
    ) {
      this.handleConnectionEnd(targetPeerId);
    }
  }

  private isDisconnectedState(state: string): state is DisconnectedState {
    return DISCONNECTED_STATES.includes(state as DisconnectedState);
  }

  // Stats Management
  private updateLatestStats(peerId: string, statsData: WebRTCStatsData): void {
    const connection = this.connections.get(peerId);
    if (!connection) {
      console.log(`No connection found for peerId: ${peerId}`);
      return;
    }
    connection.latestStatsData = statsData;
  }

  // Call Score Calculation
  private handleConnectionEnd(peerId: string): void {
    const connection = this.connections.get(peerId);
    if (!connection) {
      console.log(`No connection found in handleConnectionEnd for ${peerId}`);
      return;
    }

    try {
      this.calculateAndNotifyScore(peerId, connection);
    } catch (error) {
      console.error(`Error calculating call score for peer ${peerId}:`, error);
    } finally {
      this.removeConnection(peerId);
    }
  }

  private calculateAndNotifyScore(
    peerId: string,
    connection: ConnectionInfo
  ): void {
    if (!connection.latestStatsData || !this.dispatch) {
      console.log(`No latestStatsData or dispatch available for ${peerId}`);
      return;
    }

    const callStats = convertStatsObjectToCallStats(connection.latestStatsData);
    const callScore = calculateCallScore(callStats);
    sendWebRTCCallScore(peerId, callScore, this.dispatch, connection.callId);
  }

  // Public API Methods
  public calculateFinalScore(peerId: string): void {
    this.handleConnectionEnd(peerId);
  }

  public getLatestStatsData(peerId: string): WebRTCStatsData | null {
    return this.connections.get(peerId)?.latestStatsData ?? null;
  }

  public getAllConnections(): string[] {
    return Array.from(this.connections.keys());
  }

  public async getRawWebRTCStats(
    peerId: string
  ): Promise<RTCStatsReport | null> {
    const connection = this.connections.get(peerId);
    if (!connection) {
      return null;
    }

    try {
      return await connection.pc.getStats();
    } catch (error) {
      console.error(
        `Error getting raw WebRTC stats for peer ${peerId}:`,
        error
      );
      return null;
    }
  }

  public getLatestConnectionMetrics(peerId: string): ConnectionMetrics | null {
    const connection = this.connections.get(peerId);
    return getLatestConnectionMetrics(connection?.latestStatsData);
  }
}
